(function (global) {
  'use strict';

  const list = (value) => Array.isArray(value) ? value : [];
  const money = (value) => Math.round((Number(value) || 0) * 100) / 100;
  const accountName = (posting) => {
    const code = String(posting && (posting.nr || posting.accountCode) || '').trim();
    const name = String(posting && posting.name || '').trim();
    if (!code || !name.startsWith(code)) return name;
    return name.slice(code.length).replace(/^\s*(?:[·.\-–—:]\s*)?/, '').trim();
  };

  // Enrich display identity only; recorded amounts and account postings remain unchanged.
  const supplierPostings = (voucher, suppliers = []) => list(voucher && voucher.postings).map(posting => {
    if (String(posting.nr || posting.accountCode) !== '2400') return posting;
    const source = voucher.source || {};
    const sides = Number(posting.amount) < 0 ? ['from'] : ['to'];
    const legs = list(source.manualPostingLines).flatMap(line => sides.filter(side => String(line[`${side}Account`]) === '2400').map(side => ({id:line[`${side}CounterpartyId`],number:line[`${side}CounterpartyNumber`],name:line[`${side}CounterpartyName`]})));
    const ids = [...new Set(legs.map(leg => leg.id))];
    const partyId = posting.party_id || posting.partyId || source.supplierId || (ids.length === 1 ? ids[0] : '');
    const supplier = suppliers.find(entry => entry.id === partyId);
    const leg = legs.find(entry => entry.id === partyId);
    const number = supplier?.supplierNumber || supplier?.nr || leg?.number;
    const name = supplier?.name || leg?.name || source.supplierName;
    return partyId && number ? {...posting,nr:`2400:${number}`,name:name || posting.name} : posting;
  });

  const groupAccounts = (voucher) => {
    const accounts = new Map();
    list(voucher && voucher.postings).forEach((posting) => {
      const code = String(posting.nr || posting.accountCode || '').trim() || '0000';
      const name = accountName(posting);
      const key = `${code}:${name}`;
      const current = accounts.get(key) || { code, name, amount: 0 };
      current.amount = money(current.amount + money(posting.amount));
      accounts.set(key, current);
    });
    return Array.from(accounts.values()).map((account) => ({
      ...account,
      incoming: account.amount > 0 ? account.amount : 0,
      outgoing: account.amount < 0 ? Math.abs(account.amount) : 0,
    }));
  };

  const vatCodes = (voucher) => Array.from(new Set(
    list(voucher && voucher.vatReportCodes).map((code) => String(code || '').trim()).filter(Boolean),
  ));

  const documentEntries = (voucher) => {
    const attachments = list(voucher && voucher.attachments);
    const source = voucher && voucher.source || {};
    const normalized = attachments.map((attachment, index) => ({
      ...attachment,
      id: attachment.id || attachment.storagePath || attachment.downloadUrl || `attachment-${index + 1}`,
      name: attachment.name || voucher.attachmentName || source.attachmentName || `Bilag ${index + 1}`,
      mimeType: attachment.mimeType || attachment.type || '',
      size: Number(attachment.size) || 0,
    }));
    if (normalized.length || !voucher || !voucher.hasAttachment) return normalized.filter((document, index) => normalized.findIndex(entry => entry.id === document.id) === index);
    return [{
      id: voucher.attachmentId || source.attachmentId || 'primary-attachment',
      name: voucher.attachmentName || source.attachmentName || 'Bilag',
      mimeType: voucher.attachmentMimeType || source.attachmentMimeType || '',
      size: Number(voucher.attachmentSize || source.attachmentSize) || 0,
    }];
  };

  const isPayment = (voucher) => /^(payment|expensePayment|payrollPayment|payrollTaxPayment)(History)?(Reversal)?$/.test(voucher?.sourceType || '') || ['betaling', 'lønnsbetaling', 'betaling-offentlige-avgifter'].includes(voucher?.kind);
  const paymentParent = (payment, rows) => {
    if (!isPayment(payment)) return null;
    const id = String(payment.source?.documentId || payment.source?.document_id || (/^expensePayment/.test(payment.sourceType) ? payment.sourceId : '') || '');
    if (!id) return null;
    return rows.find((row) => !isPayment(row) && !row.reversesEntryId && String(row.sourceId) === id && ['expense', 'manualPosting', 'invoice', 'otherSale'].includes(row.sourceType)) || null;
  };
  const displayName = (voucher, rows) => {
    const parent = paymentParent(voucher, rows);
    const description = parent?.source?.description || parent?.name;
    return description && description !== voucher.name ? `${voucher.name} - ${description}` : voucher.name;
  };
  // Resolve only persisted journal links, across all years; never synthesize a reversal.
  const annulment = (voucher, events, rows, state = {}) => {
    if (!voucher || voucher.reversesEntryId) return null;
    const sourceKey = { manualPosting: 'expenses', expense: 'expenses', otherSale: 'otherSales', invoice: 'invoices', customsDeclaration: 'customsDeclarations' }[voucher.sourceType];
    const source = list(state[sourceKey]).find(entry => entry.id === voucher.sourceId) || voucher.source || {};
    const reversals = list(events).filter(event => event.reverses_event_id === (voucher.eventId || voucher.id));
    if (!reversals.length && !['annulled', 'annullert', 'reversed'].includes(source.status || voucher.status)) return null;
    return {
      at: source.annulledAt || '',
      reason: source.annulComment || '',
      reversals: reversals.map(event => ({
        id: event.event_id, number: event.recorded_seq, date: event.accounting_date,
        at: event.recorded_at_utc || '', actor: event.audit?.actor_user_id || '',
        postings: list(event.posting_lines),
        voucher: list(rows).find(row => row.id === event.event_id) || null,
      })),
    };
  };
  const actorEmail = (actor, backend = {}) => {
    if (!actor) return '';
    if (backend.user?.uid === actor) return backend.user.email || '';
    return list(backend.businessMembers).find(member => member.businessId === backend.activeBusinessId && member.userId === actor)?.email || '';
  };
  global.PlummeTransactionPresentation = Object.freeze({ actorEmail, annulment, supplierPostings, accountName, groupAccounts, vatCodes, documentEntries, isPayment, paymentParent, displayName });
})(typeof window !== 'undefined' ? window : globalThis);
